import { TENANTS } from '../config';
import { getSession } from '../auth';
import { getTheme } from '../theme';
import { toast } from '../components/toast';

const PREFS_KEY = 'clt-prefs';

function pref(key: string): boolean {
  try { const p = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}'); return !!p[key]; } catch { return false; }
}
function setPref(key: string, v: boolean) {
  try { const p = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}'); p[key] = v; localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch {}
}

function policy(name: string, body: string) {
  return `
    <details class="policy">
      <summary class="t-strong" style="cursor:pointer; padding: 14px 16px; background: var(--bg-sunken); border-radius: 10px; border: 1px solid var(--rule);">${name}</summary>
      <div style="padding: 14px 4px 4px; font-size: var(--t-sm); color: var(--ink-2); line-height: 1.7;">${body}</div>
    </details>`;
}

export function renderSettings() {
  const session = getSession();
  if (!session) return '<div class="card card-pad-lg empty"><h2>Not signed in</h2></div>';
  const tenantId = getSession()?.tenantId || 'techtatwa';
  const t = TENANTS[tenantId];
  const toggles: Array<{ key: string; label: string; sub: string }> = [
    { key: 'emailSlips', label: 'Email payslips each week', sub: 'Send slip PDF at run approval' },
    { key: 'emailLeads', label: 'New-lead alerts to team lead', sub: 'Firebase Cloud Function → inbox' },
    { key: 'weeklyDigest', label: 'Weekly digest', sub: 'Attendance + revenue summary, Monday 9am' },
    { key: 'smsReminders', label: 'SMS reminders', sub: 'Class reminders, IAP-style' },
  ];
  const tog = toggles.map((x) => `
    <div class="row" style="padding: 12px 0; border-bottom: 1px solid var(--rule);">
      <div style="flex:1"><div class="t-strong" style="font-size: var(--t-sm);">${x.label}</div><div class="t-meta" style="font-size: 11px;">${x.sub}</div></div>
      <button class="toggle ${pref(x.key) ? 'on' : ''}" data-pref="${x.key}" role="switch" aria-checked="${pref(x.key)}" style="width:46px;height:26px;border-radius:999px;background:${pref(x.key) ? 'var(--success)' : 'var(--rule-2)'};border:0;position:relative;cursor:pointer;"></button>
    </div>`).join('');

  const policies = policy('Terms of Service',
    'Clientale is a subscription SaaS for course providers. Demo build: all data is local to your browser and deleted on sign-out. In production your tenant agrees: you own your student records; Clientale stores them under your tenant namespace; we never sell or share personal data. Fair-use only; refunds per your plan. Governing law: India.' )
    + policy('Privacy Policy',
    'We process: (1) Account data — name, email, role; (2) Student/lead data you enter — used only to operate your workspace; (3) Usage telemetry — anonymised. We store data with Google Firebase (EU/Mumbai region option). You may export or delete all data from the Settings > Privacy screen. Retention: 90 days after account closure. Contact: privacy@clientale.in.')
    + policy('Data Processing Agreement (DPA)',
    'Clientale acts as Processor for your (Controller) personal data: recruitment leads, learner records, payroll. Processing purposes: account operation, attendance, billing, statutory payroll (PF/PT/TDS) documentation. Sub-processors: Google Cloud (Firebase). International transfer: only within India/Google EU regions you select. Breach notification within 72h. Terms incorporate GDPR Art. 28 and India DPDP Act 2023 obligations.')
    + policy('Cookie & Storage Policy',
    'This app stores functional tokens only: theme, tenant selection, session, and feature preferences — in your browser localStorage. No advertising cookies, no third-party trackers. The public lead form stores the enquiry so your team lead can follow up. You can clear everything with "Reset demo data" below.');

  return `
    <section class="hero stagger">
      <div>
        <div class="hero-greet">Settings · ${tenantId}</div>
        <h1 class="hero-title">Tune your<br/><span class="hero-stroke">workspace.</span></h1>
      </div>
      <div class="hero-aside">
        <button class="btn btn-ghost touch-target" data-action="switch-tenant">↗ Switch tenant</button>
        <button class="btn btn-primary touch-target" data-action="sign-out">Sign out</button>
      </div>
    </section>

    <div class="treasury-grid stagger" style="grid-template-columns: 1.1fr 1fr;">
      <div class="stack">
        <div class="card card-pad-lg">
          <div class="t-eyebrow" style="margin-bottom: var(--s-3);">Profile</div>
          <div class="row" style="padding: 8px 0;"><div class="avatar av-c3">${session.initials}</div><div style="flex:1"><div class="t-strong">${session.name}</div><div class="t-meta">${session.email}</div></div><span class="pill pill-info">${session.role}</span></div>
          <div class="divider-rule" style="margin: var(--s-3) 0;">Tenant</div>
          <div class="row" style="padding: 6px 0;"><div class="t-strong" style="flex:1">${t.nameLatin}</div><span class="pill pill-accent">${t.tagline.split(' · ').slice(-1)[0]}</span></div>
          <div class="divider-rule" style="margin: var(--s-3) 0;">Appearance</div>
          <div class="row">
            <div class="t-strong" style="flex:1">Theme</div>
            <div class="theme-switch">
              <button data-action="set-theme" data-theme="light" class="${getTheme() === 'light' ? 'active' : ''}">Light</button>
              <button data-action="set-theme" data-theme="dark" class="${getTheme() === 'dark' ? 'active' : ''}">Dark</button>
            </div>
          </div>
        </div>

        <div class="card card-pad-lg">
          <div class="t-eyebrow" style="margin-bottom: var(--s-2);">Notifications</div>
          ${tog}
        </div>

        <div class="card card-pad-lg">
          <div class="t-eyebrow" style="margin-bottom: var(--s-3);">Privacy & data</div>
          <div class="stack-tight">
            <button class="btn btn-ghost touch-target" data-action="export-data">Export my data (JSON)</button>
            <button class="btn btn-ghost touch-target" data-action="reset-demo" style="color: var(--danger); border-color: var(--danger);">Reset demo data</button>
          </div>
          <div class="t-meta" style="margin-top: var(--s-3); font-size: 11px;">Export writes all local records to a downloadable file. Reset clears every overlay + captured lead in this browser.</div>
        </div>
      </div>

      <div class="card card-pad-lg">
        <div class="t-eyebrow" style="margin-bottom: var(--s-3);">Policies</div>
        <div class="stack-tight">${policies}</div>
        <div class="t-meta" style="margin-top: var(--s-3); font-size: 11px;">Last reviewed 29 Aug 2026 · demo build v8. Enable real hosted policies when the legal docs are finalised.</div>
      </div>
    </div>
  `;
}

export function bindSettings() {
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    const prefEl = t.closest<HTMLElement>('[data-pref]');
    if (prefEl) {
      const v = !pref(prefEl.dataset.pref!);
      setPref(prefEl.dataset.pref!, v);
      const btn = prefEl as HTMLButtonElement;
      btn.style.background = v ? 'var(--success)' : 'var(--rule-2)';
      btn.setAttribute('aria-checked', String(v));
      toast(`${prefEl.dataset.pref} ${v ? 'on' : 'off'}`, 'success');
      return;
    }
    if (t.closest('[data-action="switch-tenant"]')) { (window as any).__repaint?.(); location.hash = '#/login'; return; }
    if (t.closest('[data-action="sign-out"]')) { localStorage.removeItem('clt-session'); location.hash = '#/login'; toast('Signed out', 'warn'); return; }
    if (t.closest('[data-action="export-data"]')) {
      const keys = ['clt-session','clt-theme','clt-tenant','clt-attendance-overlay','clt-prefs','clt-leads','clt-public-leads'];
      const out: Record<string, unknown> = {};
      keys.forEach((k) => { try { out[k] = localStorage.getItem(k) ? JSON.parse(localStorage.getItem(k) as string) : null; } catch { out[k] = localStorage.getItem(k); } });
      const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'clientale_export_' + new Date().toISOString().slice(0, 10) + '.json'; a.click();
      URL.revokeObjectURL(a.href);
      toast('Export downloaded', 'success');
      return;
    }
    if (t.closest('[data-action="reset-demo"]')) {
      ['clt-attendance-overlay','clt-prefs','clt-leads','clt-public-leads'].forEach((k) => localStorage.removeItem(k));
      toast('Demo data reset', 'warn');
      setTimeout(() => (window as any).__repaint?.(), 300);
      return;
    }
  });
}

void [];
